import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Save,
  Mail,
  Image,
  RefreshCw,
  Plus,
  Trash2,
  ExternalLink,
  Check,
  AlertCircle,
  Settings,
  LogOut,
  DollarSign,
  Upload,
  Loader2
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getAdminToken, clearAdminToken } from "@/pages/AdminLoginPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultServices = [
  { title: "Video Production", description: "Professional videography for commercials, events, and brand content", image: "https://images.unsplash.com/photo-1634812932028-3baa37d90b52?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", price_type: "starting", price: 500 },
  { title: "Web Design", description: "Custom websites for sales, appointments, and information", image: "https://images.unsplash.com/photo-1553525302-38381e3e0c9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "starting", price: 1000 },
  { title: "AI Bot Solutions", description: "Intelligent chatbots and automation implementation", image: "https://images.unsplash.com/photo-1701505708176-63194ee8f0e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "quote", price: null },
  { title: "YouTube Content", description: "Engaging video content optimized for YouTube growth", image: "https://images.unsplash.com/photo-1611162616475-46b635cb6868?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", price_type: "starting", price: 300 },
  { title: "Instagram Content", description: "Eye-catching reels, stories, and posts for Instagram", image: "https://images.unsplash.com/photo-1611262588024-d12430b98920?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "fixed", price: 150 },
  { title: "Facebook Content", description: "Strategic content creation for Facebook engagement", image: "https://images.unsplash.com/photo-1579869847514-7c1a19d2d2ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "fixed", price: 150 },
  { title: "Audio & Jingles", description: "Custom jingle creation and audio production", image: "https://images.unsplash.com/photo-1591960028610-17925389af25?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", price_type: "starting", price: 250 },
  { title: "Video Editing", description: "Professional post-production and editing services", image: "https://images.unsplash.com/photo-1536724609414-5f000e9a2745?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "starting", price: 200 },
  { title: "OBS Consultation", description: "Expert setup and optimization for streaming with OBS", image: "https://images.unsplash.com/photo-1587620962725-abab7fe55159?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "fixed", price: 100 },
  { title: "Photography", description: "High-quality photography for products and events", image: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "starting", price: 300 },
  { title: "Logo Design", description: "Company logos and brand imagery that stand out", image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "fixed", price: 350 },
  { title: "Scripting", description: "Compelling scripts for ads, videos, and presentations", image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "starting", price: 150 },
  { title: "Commercial Ads", description: "Full-service advertisement production", image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&q=85&w=600", price_type: "quote", price: null }
];

const defaultPortfolioImages = [
  "https://images.unsplash.com/photo-1598814941971-d5d4f0614229?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1739470413072-5ec7c3766d54?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1701505708176-63194ee8f0e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1591960028610-17925389af25?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1536724609414-5f000e9a2745?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [uploading, setUploading] = useState(null); // Track which field is uploading
  
  const heroFileRef = useRef(null);
  const aboutFileRef = useRef(null);
  const portfolioFileRef = useRef(null);
  const serviceFileRefs = useRef({});
  
  const [settings, setSettings] = useState({
    contact_email: "streamcaster.dmca+business@gmail.com",
    hero_image: "https://images.unsplash.com/photo-1739470413072-5ec7c3766d54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    about_image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    services: defaultServices,
    portfolio_images: defaultPortfolioImages
  });

  const [newPortfolioUrl, setNewPortfolioUrl] = useState("");

  // Image upload function
  const uploadImage = async (file, targetField, serviceIndex = null) => {
    const token = getAdminToken();
    const formData = new FormData();
    formData.append("file", file);
    
    setUploading(targetField + (serviceIndex !== null ? `-${serviceIndex}` : ""));
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const imageUrl = `${BACKEND_URL}${res.data.url}`;
      
      if (targetField === "hero_image") {
        setSettings({ ...settings, hero_image: imageUrl });
      } else if (targetField === "about_image") {
        setSettings({ ...settings, about_image: imageUrl });
      } else if (targetField === "portfolio") {
        setSettings({ 
          ...settings, 
          portfolio_images: [...settings.portfolio_images, imageUrl] 
        });
      } else if (targetField === "service" && serviceIndex !== null) {
        const newServices = [...settings.services];
        newServices[serviceIndex] = { ...newServices[serviceIndex], image: imageUrl };
        setSettings({ ...settings, services: newServices });
      }
      
      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(null);
    }
  };

  const handleFileChange = (e, targetField, serviceIndex = null) => {
    const file = e.target.files[0];
    if (file) {
      uploadImage(file, targetField, serviceIndex);
    }
  };

  const verifyAuth = async () => {
    const token = getAdminToken();
    if (!token) {
      navigate("/admin/login");
      return false;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      clearAdminToken();
      navigate("/admin/login");
      return false;
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings({
        contact_email: res.data.contact_email || "streamcaster.dmca+business@gmail.com",
        hero_image: res.data.hero_image || settings.hero_image,
        about_image: res.data.about_image || settings.about_image,
        services: res.data.services?.length > 0 ? res.data.services : defaultServices,
        portfolio_images: res.data.portfolio_images?.length > 0 ? res.data.portfolio_images : defaultPortfolioImages
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyAuth().then(valid => {
      if (valid) fetchSettings();
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const token = getAdminToken();
    
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const updateService = (index, field, value) => {
    const newServices = [...settings.services];
    newServices[index] = { ...newServices[index], [field]: value };
    setSettings({ ...settings, services: newServices });
  };

  const addPortfolioImage = () => {
    if (newPortfolioUrl && newPortfolioUrl.startsWith("http")) {
      setSettings({
        ...settings,
        portfolio_images: [...settings.portfolio_images, newPortfolioUrl]
      });
      setNewPortfolioUrl("");
    } else {
      toast.error("Please enter a valid image URL");
    }
  };

  const removePortfolioImage = (index) => {
    const newImages = settings.portfolio_images.filter((_, i) => i !== index);
    setSettings({ ...settings, portfolio_images: newImages });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <RefreshCw size={32} className="animate-spin text-[#0A3F2F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <header className="bg-[#0A3F2F] text-white py-4 px-4 md:px-8 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-to-admin">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Admin</span>
            </Link>
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <h1 className="font-['Syne'] text-lg md:text-xl font-bold flex items-center gap-2">
              <Settings size={20} />
              Site Settings
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-[#D4FF33] text-[#0A3F2F] px-4 py-2 rounded-lg font-medium hover:bg-[#c4ef23] transition-colors disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
              <span className="hidden sm:inline">{saving ? "Saving..." : "Save Changes"}</span>
            </button>
            <button
              onClick={handleLogout}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "general", label: "General", icon: Mail },
            { id: "images", label: "Hero & About Images", icon: Image },
            { id: "services", label: "Services", icon: Settings },
            { id: "portfolio", label: "Portfolio", icon: Image }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id 
                  ? "bg-[#0A3F2F] text-white" 
                  : "bg-white text-[#0A3F2F] hover:bg-[#EBEBE3]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* General Settings */}
        {activeTab === "general" && (
          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F] mb-6">Contact Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0A3F2F] mb-2">
                  Contact Email Address
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
                  <input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <p className="text-xs text-[#4A5F55] mt-2">
                  This email will be displayed on the contact section and used for the mailto links.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Hero & About Images */}
        {activeTab === "images" && (
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Hero Image */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F] mb-4">Hero Background Image</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Image URL or Upload</label>
                  <input
                    type="url"
                    value={settings.hero_image}
                    onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] text-sm"
                    placeholder="https://..."
                    data-testid="hero-image-input"
                  />
                  <div className="mt-3">
                    <input 
                      type="file" 
                      ref={heroFileRef}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "hero_image")}
                      className="hidden"
                    />
                    <button
                      onClick={() => heroFileRef.current?.click()}
                      disabled={uploading === "hero_image"}
                      className="flex items-center gap-2 bg-[#0A3F2F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0d5139] transition-colors disabled:opacity-50"
                      data-testid="upload-hero-btn"
                    >
                      {uploading === "hero_image" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      {uploading === "hero_image" ? "Uploading..." : "Upload from Computer"}
                    </button>
                  </div>
                  <p className="text-xs text-[#4A5F55] mt-2">
                    Recommended: 1920x1080px or larger. Max 10MB.
                  </p>
                </div>
                <div className="aspect-video rounded-lg overflow-hidden bg-[#EBEBE3]">
                  <img 
                    src={settings.hero_image} 
                    alt="Hero preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = "https://via.placeholder.com/400x225?text=Invalid+URL"}
                  />
                </div>
              </div>
            </div>

            {/* About Image */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F] mb-4">About Section Image</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Image URL or Upload</label>
                  <input
                    type="url"
                    value={settings.about_image}
                    onChange={(e) => setSettings({ ...settings, about_image: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] text-sm"
                    placeholder="https://..."
                    data-testid="about-image-input"
                  />
                  <div className="mt-3">
                    <input 
                      type="file" 
                      ref={aboutFileRef}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "about_image")}
                      className="hidden"
                    />
                    <button
                      onClick={() => aboutFileRef.current?.click()}
                      disabled={uploading === "about_image"}
                      className="flex items-center gap-2 bg-[#0A3F2F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0d5139] transition-colors disabled:opacity-50"
                      data-testid="upload-about-btn"
                    >
                      {uploading === "about_image" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      {uploading === "about_image" ? "Uploading..." : "Upload from Computer"}
                    </button>
                  </div>
                  <p className="text-xs text-[#4A5F55] mt-2">
                    Recommended: Square image, 800x800px. Max 10MB.
                  </p>
                </div>
                <div className="aspect-square rounded-lg overflow-hidden bg-[#EBEBE3] max-w-[200px]">
                  <img 
                    src={settings.about_image} 
                    alt="About preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = "https://via.placeholder.com/200?text=Invalid+URL"}
                  />
                </div>
              </div>
            </div>

            {/* Image Sources Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Upload size={20} className="text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Upload your own images!</p>
                  <p className="text-sm text-green-800 mt-1">
                    Click "Upload from Computer" to upload images directly from your device. 
                    Supported formats: JPG, PNG, GIF, WebP, SVG. Max file size: 10MB.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Services */}
        {activeTab === "services" && (
          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F]">Service Cards & Pricing</h2>
              <div className="flex items-center gap-2 text-sm text-[#4A5F55]">
                <DollarSign size={16} />
                <span>Set prices for your services</span>
              </div>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {settings.services.map((service, index) => (
                <div key={index} className="border border-[#D1D1C7] rounded-xl p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#EBEBE3] flex-shrink-0">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.src = "https://via.placeholder.com/64?text=?"}
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={service.title}
                        onChange={(e) => updateService(index, "title", e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-[#D1D1C7] font-medium text-[#0A3F2F] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                        placeholder="Service title"
                      />
                      <input
                        type="text"
                        value={service.description}
                        onChange={(e) => updateService(index, "description", e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-[#D1D1C7] text-sm text-[#4A5F55] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                        placeholder="Service description"
                      />
                      <input
                        type="url"
                        value={service.image}
                        onChange={(e) => updateService(index, "image", e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-[#D1D1C7] text-xs text-[#4A5F55] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                        placeholder="Image URL"
                      />
                      
                      {/* Pricing Controls */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#EBEBE3]">
                        <select
                          value={service.price_type || "quote"}
                          onChange={(e) => updateService(index, "price_type", e.target.value)}
                          className="px-3 py-2 rounded-lg border border-[#D1D1C7] text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] bg-white"
                          data-testid={`price-type-${index}`}
                        >
                          <option value="fixed">Fixed Price</option>
                          <option value="starting">Starting From</option>
                          <option value="quote">Contact for Quote</option>
                        </select>
                        
                        {(service.price_type === "fixed" || service.price_type === "starting") && (
                          <div className="relative flex-1 min-w-[120px]">
                            <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
                            <input
                              type="number"
                              value={service.price || ""}
                              onChange={(e) => updateService(index, "price", parseFloat(e.target.value) || null)}
                              className="w-full pl-8 pr-3 py-2 rounded-lg border border-[#D1D1C7] text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                              data-testid={`price-input-${index}`}
                            />
                          </div>
                        )}
                        
                        <span className="text-xs text-[#4A5F55] self-center">
                          {service.price_type === "fixed" && service.price && `$${service.price.toFixed(2)}`}
                          {service.price_type === "starting" && service.price && `From $${service.price.toFixed(2)}`}
                          {service.price_type === "quote" && "Custom quote"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Portfolio */}
        {activeTab === "portfolio" && (
          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F] mb-6">Portfolio Images</h2>
            
            {/* Add new image - URL or Upload */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex gap-2 flex-1">
                <input
                  type="url"
                  value={newPortfolioUrl}
                  onChange={(e) => setNewPortfolioUrl(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                  placeholder="Paste image URL to add..."
                  data-testid="new-portfolio-url"
                />
                <button
                  onClick={addPortfolioImage}
                  className="flex items-center gap-2 bg-[#0A3F2F] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#0d5139] transition-colors"
                  data-testid="add-portfolio-btn"
                >
                  <Plus size={18} />
                  Add URL
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#4A5F55]">or</span>
                <input 
                  type="file" 
                  ref={portfolioFileRef}
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "portfolio")}
                  className="hidden"
                />
                <button
                  onClick={() => portfolioFileRef.current?.click()}
                  disabled={uploading === "portfolio"}
                  className="flex items-center gap-2 bg-[#D4FF33] text-[#0A3F2F] px-4 py-2 rounded-lg font-medium hover:bg-[#c4ef23] transition-colors disabled:opacity-50"
                  data-testid="upload-portfolio-btn"
                >
                  {uploading === "portfolio" ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                  {uploading === "portfolio" ? "Uploading..." : "Upload Image"}
                </button>
              </div>
            </div>

            {/* Image grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {settings.portfolio_images.map((image, index) => (
                <div key={index} className="relative group aspect-video rounded-lg overflow-hidden bg-[#EBEBE3]">
                  <img 
                    src={image} 
                    alt={`Portfolio ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = "https://via.placeholder.com/300x200?text=Invalid"}
                  />
                  <button
                    onClick={() => removePortfolioImage(index)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    data-testid={`remove-portfolio-${index}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
